import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Wifi, RefreshCw, Plus, Loader2, CheckCircle2, XCircle, Unplug, Search, ExternalLink } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ConnexionBancaire() {
  const [searchParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [providerInfo, setProviderInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState('');
  // Connect dialog
  const [connectOpen, setConnectOpen] = useState(false);
  const [banks, setBanks] = useState([]);
  const [bankSearch, setBankSearch] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [connecting, setConnecting] = useState(false);

  const fetchAll = async () => {
    try {
      const [cRes, pRes, stRes] = await Promise.all([
        axios.get(`${API_URL}/banking/connections`),
        axios.get(`${API_URL}/banking/provider`),
        axios.get(`${API_URL}/lettrage/stats`),
      ]);
      setConnections(cRes.data); setProviderInfo(pRes.data); setStats(stRes.data);
      // Load transactions from first active connection
      const active = cRes.data.find(c => c.status === 'active');
      if (active) loadTransactions(active.id);
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Handle callback from bank auth
  useEffect(() => {
    if (searchParams.get('callback') === '1') {
      // Find pending connection and finalize
      const finalize = async () => {
        const conns = (await axios.get(`${API_URL}/banking/connections`)).data;
        const pending = conns.find(c => c.status === 'pending_auth');
        if (pending) {
          try {
            await axios.post(`${API_URL}/banking/callback/${pending.id}`);
            toast.success('Connexion bancaire établie');
            fetchAll();
          } catch (e) { toast.error('Erreur finalisation connexion'); }
        }
      };
      finalize();
    }
  }, [searchParams]);

  const loadTransactions = async (connId) => {
    try {
      const res = await axios.get(`${API_URL}/banking/transactions`, { params: { connection_id: connId, limit: 100 } });
      setTransactions(res.data);
    } catch (e) {}
  };

  const handleSync = async (connId) => {
    setSyncing(connId);
    try {
      const res = await axios.post(`${API_URL}/banking/connections/${connId}/sync`);
      toast.success(`${res.data.new_transactions} nouvelles transactions (${res.data.provider})`);
      loadTransactions(connId);
      const stRes = await axios.get(`${API_URL}/lettrage/stats`);
      setStats(stRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur synchronisation'); }
    finally { setSyncing(''); }
  };

  const openConnect = async () => {
    setConnectOpen(true);
    setBanks([]); setBankSearch(''); setSelectedBank('');
    if (providerInfo?.provider === 'gocardless') {
      try {
        const res = await axios.get(`${API_URL}/banking/institutions`, { params: { country: 'FR' } });
        setBanks(res.data.banks || []);
      } catch (e) { toast.error('Erreur chargement des banques'); }
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const body = { callback_url: `${window.location.origin}/connexion-bancaire?callback=1` };
      if (providerInfo?.provider === 'gocardless' && selectedBank) {
        body.institution_id = selectedBank;
      }
      const res = await axios.post(`${API_URL}/banking/connect`, body);
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur connexion'); }
    finally { setConnecting(false); }
  };

  const filteredBanks = banks.filter(b => !bankSearch || b.name.toLowerCase().includes(bankSearch.toLowerCase()));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold"><Wifi className="w-6 h-6 inline mr-2" />Connexion bancaire</h1>
          <p className="text-slate-500">
            Provider : <Badge variant="outline" className="ml-1">{providerInfo?.provider || '—'}</Badge>
            {providerInfo?.configured
              ? <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Configuré</Badge>
              : <Badge className="ml-2 bg-red-100 text-red-700 text-xs">Non configuré</Badge>
            }
          </p>
        </div>
        <Button onClick={openConnect} disabled={!providerInfo?.configured} className="bg-[#0F5156] hover:bg-[#0d464a]">
          <Plus className="w-4 h-4 mr-2" /> Connecter une banque
        </Button>
      </div>

      {!providerInfo?.configured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-700">Le provider bancaire n'est pas configuré. Demandez au super admin d'ajouter les clés API dans Super Admin > Banking.</p>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-sm text-slate-500">Transactions</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-green-600">{stats.matched}</p><p className="text-sm text-slate-500">Lettrées</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-amber-600">{stats.unmatched}</p><p className="text-sm text-slate-500">Non lettrées</p></CardContent></Card>
          <Card><CardContent className="pt-6"><Progress value={stats.match_rate} className="mb-2" /><p className="text-center text-sm">{stats.match_rate}% lettré</p></CardContent></Card>
        </div>
      )}

      {/* Connections */}
      <Card>
        <CardHeader><CardTitle>Comptes connectés</CardTitle></CardHeader>
        <CardContent>
          {connections.length === 0 ? (
            <div className="text-center py-8">
              <Unplug className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">Aucun compte connecté. Cliquez sur "Connecter une banque" pour commencer.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {connections.map(c => (
                <div key={c.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {c.status === 'active' ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                      : c.status === 'pending_auth' ? <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                      : <XCircle className="w-5 h-5 text-red-500" />}
                    <div>
                      <p className="font-medium">{c.bank_name || c.bank_account_label || 'Compte bancaire'}</p>
                      <div className="flex gap-3 text-xs text-slate-400 mt-0.5">
                        {c.iban_last4 && <span>IBAN ****{c.iban_last4}</span>}
                        <span>{c.provider}</span>
                        <span>{c.transaction_count || 0} transactions</span>
                        {c.last_sync && <span>Sync : {new Date(c.last_sync).toLocaleString('fr-FR')}</span>}
                      </div>
                      {c.sync_error && <p className="text-xs text-red-500 mt-0.5">{c.sync_error}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {c.status === 'pending_auth' && c.auth_url && (
                      <Button variant="outline" size="sm" onClick={() => window.location.href = c.auth_url}>
                        <ExternalLink className="w-4 h-4 mr-1" /> Autoriser
                      </Button>
                    )}
                    {c.status === 'active' && (
                      <Button variant="outline" size="sm" onClick={() => handleSync(c.id)} disabled={syncing === c.id}>
                        {syncing === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                        Synchroniser
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      {transactions.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Dernières transactions</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Libellé</TableHead><TableHead>Catégorie</TableHead><TableHead className="text-right">Montant</TableHead><TableHead>Lettrage</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {transactions.slice(0, 50).map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">{t.date_operation}</TableCell>
                    <TableCell className="text-sm">{t.libelle_clean || t.libelle}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{t.categorie || '-'}</Badge></TableCell>
                    <TableCell className={`text-right font-mono font-medium ${t.montant >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {t.montant >= 0 ? '+' : ''}{formatCurrency(t.montant)}
                    </TableCell>
                    <TableCell>
                      {t.is_matched ? <Badge className="bg-green-100 text-green-700 text-xs">Lettré</Badge>
                        : <Badge className="bg-slate-100 text-slate-500 text-xs">—</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Connect Dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle><Wifi className="w-5 h-5 inline mr-2" />Connecter une banque</DialogTitle>
          </DialogHeader>

          {providerInfo?.provider === 'gocardless' ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Sélectionnez votre banque. Vous serez redirigé pour vous authentifier.</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Rechercher une banque..." className="pl-10" value={bankSearch} onChange={e => setBankSearch(e.target.value)} />
              </div>
              <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                {filteredBanks.slice(0, 30).map(b => (
                  <button key={b.id} type="button" onClick={() => setSelectedBank(b.id)}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors ${selectedBank === b.id ? 'bg-teal-50 border-l-2 border-[#0F5156]' : ''}`}>
                    {b.logo && <img src={b.logo} alt="" className="w-8 h-8 rounded" />}
                    <span className="text-sm font-medium">{b.name}</span>
                  </button>
                ))}
                {filteredBanks.length === 0 && <p className="text-center py-4 text-sm text-slate-400">Aucune banque trouvée</p>}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Bridge va ouvrir un widget sécurisé pour vous connecter à votre banque.</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>Annuler</Button>
            <Button onClick={handleConnect} disabled={connecting || (providerInfo?.provider === 'gocardless' && !selectedBank)} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              Se connecter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
